import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, CategoryScale, Legend, ArcElement } from "chart.js";
import { PaymentMethod } from "../../../../../api/settlement";
import { useMemo, useState } from "react";
import { SettlementDailyRevenueResponse } from "../../../../../api/settlement-daily";
ChartJS.register(CategoryScale, ArcElement, Legend, ChartDataLabels);

const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  [PaymentMethod.CASH]: "현금",
  [PaymentMethod.BANK_TRANSFER]: "계좌",
  [PaymentMethod.CARD]: "카드",
  [PaymentMethod.POINTS]: "포인트(무료)",
  [PaymentMethod.SEED]: "래빗",
  [PaymentMethod.DISCOUNT]: "",
  [PaymentMethod.OTHER]: "",
};

// 차드 나열 순서
const CHART_METHODS: PaymentMethod[] = [
  PaymentMethod.CASH,
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.CARD,
  PaymentMethod.POINTS,
  PaymentMethod.SEED,
];

export const useDailyRevenueChart = (
  dailyRevenue: SettlementDailyRevenueResponse | undefined
) => {
  // 해당 데이터가 더미 데이터인지 여부
  const [isNoData, setIsNoData] = useState(false);

  // 데이터 리스트 (비율)
  const rateData = useMemo(() => {
    if (!dailyRevenue) return [];

    const values = CHART_METHODS.map((method) => {
      const revenue = dailyRevenue.paymentMethodRevenues.find(
        (p) => p.method === method
      );
      if (!revenue) return 0;

      return revenue.otherRevenue + revenue.tournamentRevenue;
    });

    const sum = values.reduce((a, b) => a + b, 0);

    // 전부 0이면 더미 데이터
    if (sum === 0) {
      setIsNoData(true);
      return [1, 1, 1, 1, 1];
    }

    setIsNoData(false);
    return values.map((v) => (v / dailyRevenue.totalRevenue) * 100);
  }, [dailyRevenue]);

  // 라벨 리스트
  const labels = CHART_METHODS.map((method) => PAYMENT_METHOD_LABEL[method]);

  // 도넛 차트 가운데 텍스트 추가
  const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart: any) {
      const { ctx, width, height } = chart;
      ctx.save();
      ctx.font = "700 32px Pretendard";
      ctx.fillStyle = "#212023";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("총매출", width / 2, height / 2);
      ctx.restore();
    },
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        data: rateData,
        backgroundColor: [
          "#7F25FB",
          "#917EF2",
          "#C3B6FF",
          "#DBD3FF",
          "#ECE9FF",
        ],
        datalabels: {
          labels: {
            name: {
              display: (context: any) =>
                context.dataset.data[context.dataIndex] !== 0,
              color: (context: any) => {
                return labels[context.dataIndex] === "현금"
                  ? "#fff"
                  : "#121212";
              },
              font: {
                size: 22,
                weight: 500,
                family: "Pretendard",
              },
              formatter: (value: any, context: any) => {
                const index = context.dataIndex;
                return labels[index];
              },
              borderRadius: 4,
              offset: 0,
              align: "top" as const,
            },
            value: {
              display: (context: any) =>
                context.dataset.data[context.dataIndex] !== 0,
              // 더미 데이터일 시 0.00으로 포맷
              formatter: (value: any) => {
                return isNoData ? "0.00%" : `${value.toFixed(2)}%`;
              },
              color: "#fff",
              font: {
                size: 20,
                weight: 700,
                family: "Pretendard",
              },
              backgroundColor: "#373636",
              borderRadius: 5,
              offset: 0,
              align: "bottom" as const,
            },
          },
        },
      },
    ],
  };

  const options = {
    cutout: 80,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    events: [],
  };

  return { chartData, options, centerTextPlugin };
};
